import { Box, HStack, VStack } from '@chakra-ui/react';
import { keyframes } from '@chakra-ui/system';
import { useQueryClient } from '@tanstack/react-query';
import { useSupabaseTypedClient } from 'hooks/reactQuery';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useCallback, useState } from 'react';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import { SEO } from 'components/SEO';
import Text from 'components/Text';
import {
  CompleteOnboardingStepParams,
  EdgeFunctionName,
  UserPreferences,
} from 'types/edgeFunctions';
import { TanstackQueryName } from 'types/frontend';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const scaleUp = keyframes`
  from { transform: scale(1); }
  to { transform: scale(1.02); }
`;

type GoalValue = 'build-product' | 'land-job' | 'level-up';
type ExperienceValue = 'beginner' | 'intermediate' | 'advanced';
type FocusValue = 'learn' | 'build' | 'career';

interface CardOption<T extends string> {
  emoji: string;
  label: string;
  subtitle: string;
  value: T;
  accentColor?: string;
}

const GOAL_OPTIONS: CardOption<GoalValue>[] = [
  {
    emoji: '\u{1F680}',
    label: 'Build My Own Product',
    subtitle: 'Ship a side project or micro-SaaS',
    value: 'build-product',
    accentColor: 'purple.400',
  },
  {
    emoji: '\u{1F4BC}',
    label: 'Land a Dev Job',
    subtitle: 'Build a portfolio that gets interviews',
    value: 'land-job',
    accentColor: 'purple.400',
  },
  {
    emoji: '\u{1F9E0}',
    label: 'Level Up My Skills',
    subtitle: 'Go deeper with frameworks and patterns',
    value: 'level-up',
    accentColor: 'purple.400',
  },
];

const EXPERIENCE_OPTIONS: CardOption<ExperienceValue>[] = [
  {
    emoji: '\u{1F331}',
    label: 'Beginner',
    subtitle: 'Learning HTML, CSS, and JavaScript',
    value: 'beginner',
    accentColor: 'purple.400',
  },
  {
    emoji: '\u{26A1}',
    label: 'Intermediate',
    subtitle: 'Comfortable with a framework, building UIs',
    value: 'intermediate',
    accentColor: 'purple.400',
  },
  {
    emoji: '\u{1F525}',
    label: 'Advanced',
    subtitle: 'Production experience, want to push further',
    value: 'advanced',
    accentColor: 'purple.400',
  },
];

const FOCUS_OPTIONS: CardOption<FocusValue>[] = [
  {
    emoji: '\u{1F4DA}',
    label: 'Guided Projects',
    subtitle: 'Step-by-step builds with real-world apps',
    value: 'learn',
    accentColor: 'purple.400',
  },
  {
    emoji: '\u{1F528}',
    label: 'AI Code Editor',
    subtitle: 'Start building right away with AI help',
    value: 'build',
    accentColor: 'purple.400',
  },
  {
    emoji: '\u{1F4BC}',
    label: 'Career Toolkit',
    subtitle: 'CV builder, portfolio projects, and practice problems',
    value: 'career',
    accentColor: 'purple.400',
  },
];

const SCREEN_CONFIG = [
  {
    title: "What's your mission?",
    subtitle: 'Pick one \u2014 you can always change later',
    options: GOAL_OPTIONS,
  },
  {
    title: 'How experienced are you?',
    subtitle: 'No wrong answer \u2014 we\u2019ll adapt to you',
    options: EXPERIENCE_OPTIONS,
  },
  {
    title: 'Where should we start?',
    subtitle: 'We\u2019ll set up your dashboard to match',
    options: FOCUS_OPTIONS,
  },
] as const;

const DEFAULT_PREFERENCES: UserPreferences = {
  goal: 'level-up',
  experience_level: 'beginner',
  focus_area: 'learn',
};

interface SelectionCardProps {
  emoji: string;
  label: string;
  subtitle: string;
  isSelected: boolean;
  onClick: () => void;
  accentColor?: string;
}

const SelectionCard = ({
  emoji,
  label,
  subtitle,
  isSelected,
  onClick,
  accentColor = 'purple.400',
}: SelectionCardProps) => (
  <Box
    as="button"
    type="button"
    onClick={onClick}
    w="100%"
    px={6}
    py={5}
    borderRadius="lg"
    border="1px solid"
    borderColor={isSelected ? accentColor : 'whiteAlpha.100'}
    bg={isSelected ? 'rgba(159, 122, 234, 0.08)' : 'whiteAlpha.50'}
    cursor="pointer"
    transition="all 0.2s"
    _hover={{
      borderColor: isSelected ? accentColor : 'whiteAlpha.200',
      bg: isSelected ? 'rgba(159, 122, 234, 0.08)' : 'whiteAlpha.100',
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    }}
    _active={{ transform: 'scale(0.99)' }}
    textAlign="left"
    animation={isSelected ? `${scaleUp} 0.3s ease forwards` : undefined}
    boxShadow={
      isSelected
        ? '0 0 0 1px rgba(159, 122, 234, 0.3), 0 4px 16px rgba(159, 122, 234, 0.1)'
        : 'none'
    }
  >
    <HStack spacing={4} alignItems="center">
      <Box style={{ fontSize: '32px' }} lineHeight={1} flexShrink={0}>
        {emoji}
      </Box>
      <VStack spacing={0.5} alignItems="flex-start" flex={1}>
        <Text fontSize="medium" fontWeight="semibold" opacity="primary">
          {label}
        </Text>
        <Text fontSize="small" opacity="tertiary">
          {subtitle}
        </Text>
      </VStack>
    </HStack>
  </Box>
);

const OnboardingPersonalizeStep = () => {
  const supabase = useSupabaseTypedClient();
  const queryClient = useQueryClient();
  const { toastError } = useToastWrapper();
  const { id: userId } = useCurrentUser();

  const [screen, setScreen] = useState(0);
  const [goal, setGoal] = useState<GoalValue | null>(null);
  const [experience, setExperience] = useState<ExperienceValue | null>(null);
  const [focus, setFocus] = useState<FocusValue | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitPreferences = useCallback(async (preferences: UserPreferences) => {
    setIsSubmitting(true);

    try {
      const { error } = await supabase.functions.invoke(EdgeFunctionName.CompleteOnboardingStep, {
        body: {
          next_step: 'paywall',
          preferences,
        } as CompleteOnboardingStepParams,
      });

      if (error) throw error;

      await queryClient.invalidateQueries({
        queryKey: [TanstackQueryName.GetCurrentUser, userId],
      });
    } catch (error: any) {
      toastError(error?.message || 'Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  }, [supabase, queryClient, userId, toastError]);

  const handleSelect = useCallback((screenIndex: number, value: string) => {
    if (isSubmitting) return;

    if (screenIndex === 0) {
      setGoal(value as GoalValue);
      setTimeout(() => setScreen(1), 400);
    } else if (screenIndex === 1) {
      setExperience(value as ExperienceValue);
      setTimeout(() => setScreen(2), 400);
    } else if (screenIndex === 2) {
      setFocus(value as FocusValue);
      // Submit with collected preferences
      setTimeout(() => {
        submitPreferences({
          goal: goal || DEFAULT_PREFERENCES.goal,
          experience_level: experience || DEFAULT_PREFERENCES.experience_level,
          focus_area: value,
        });
      }, 400);
    }
  }, [isSubmitting, goal, experience, submitPreferences]);


  const currentConfig = SCREEN_CONFIG[screen];
  const selectedValue = screen === 0 ? goal : screen === 1 ? experience : focus;

  return (
    <>
      <SEO
        title="BigDevSoon | Personalize Your Experience"
        description="Tell us about yourself to get a personalized experience"
        url={`${process.env.REACT_APP_SITE_URL}onboarding`}
      />

      <VStack
        spacing={8}
        maxW="640px"
        width="100%"
        px={4}
        py={4}
        key={screen}
        animation={`${fadeInUp} 0.4s ease-out`}
      >
        {/* Header */}
        <VStack spacing={2}>
          <Text
            fontSize="xxxxLarge"
            fontWeight="bold"
            textAlign="center"
          >
            {currentConfig.title}
          </Text>
          <Text fontSize="large" opacity="secondary" textAlign="center">
            {currentConfig.subtitle}
          </Text>
        </VStack>

        {/* Cards */}
        <VStack spacing={3} width="100%">
          {currentConfig.options.map((option) => (
            <SelectionCard
              key={option.value}
              emoji={option.emoji}
              label={option.label}
              subtitle={option.subtitle}
              isSelected={selectedValue === option.value}
              onClick={() => handleSelect(screen, option.value)}
              accentColor={option.accentColor}
            />
          ))}
        </VStack>
      </VStack>
    </>
  );
};

export default OnboardingPersonalizeStep;
